import { A3C_LOAD_BEHAVIOR_DISABLED } from 'src/commons/constants';
import { isChatDisabledUrl } from 'src/commons/utils/a3cChatUrlRules';

/**
 * The only part of `chromeConfig.a3cConfig` the gate needs. Kept structural
 * (rather than importing the slice type) so the gate stays a leaf module with
 * no dependency on the Redux slice.
 */
interface A3cChatGateConfig {
  loadBehavior?: string;
}

/**
 * Whether the AAO-owned unified chat gate explicitly vetoes chat on this page.
 *
 * AdvertisingConsoleTrimService resolves the FeatureFlagService flag
 * `isAdsAgentEnabled` — a composite of AAO's weblab and AAO's customer-level
 * Bullseye segment — once per page load in HeaderController and writes the
 * boolean from headerView.jsp, so the value is available synchronously on first
 * render.
 *
 * The check is `=== false`, i.e. fail OPEN: only an explicit `false` vetoes.
 * That is deliberate and differs from `isUnifiedA3CChatEnabled()` in
 * AdConsoleDashboardApp (src/hooks/useA3cAgent.ts), which fails closed on an
 * `=== true` check. The two behave identically on every Trim-rendered page,
 * because headerView.jsp always emits an explicit `true`/`false` literal — even
 * for a missing model attribute, since EL evaluates a null attribute compared
 * against true as false. They diverge only where the global is never set at all:
 * non-Trim hosts (BTSW, standalone) and older Trim bundles. Failing open there
 * keeps those surfaces on their pre-existing weblab-only behavior instead of
 * silently losing chat.
 *
 * Note this is NOT SSR-safe, matching the rest of ChromeUI: the module reads
 * `window` directly, as `isChatDisabledUrl(window.location.href)` already does.
 */
export function isUnifiedA3CChatVetoed(): boolean {
  return typeof window !== 'undefined' && window.isA3CEnabled === false;
}

/**
 * Single source of truth for "A3C is fully off on this page": no container, no
 * agent, no config injection, no Ads Agent toggle — even when an
 * A3C_EVERYWHERE weblab or a page-provided agent would otherwise show chat.
 *
 * Three independent vetoes:
 * - the AAO unified gate explicitly vetoed (`window.isA3CEnabled === false`)
 * - the page opted out via `a3cConfig.loadBehavior = DISABLED`
 * - the URL is on the chat-disabled list
 *
 * Consumed by EmbeddedChatManager (panel + config injection),
 * ConsolidatedSideNavInner (nav button) and useShowFocusHeaderAdsAgentToggle
 * (focus-header toggle) so all three surfaces cannot drift apart.
 */
export function isA3cChatDisabled(a3cConfig?: A3cChatGateConfig): boolean {
  return (
    isUnifiedA3CChatVetoed() ||
    a3cConfig?.loadBehavior === A3C_LOAD_BEHAVIOR_DISABLED ||
    isChatDisabledUrl(window.location.href)
  );
}
